import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Sparkles, Users, LogOut, Calculator, Settings, 
  Search, Heart, ArrowLeft 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MatchesPage = () => {
  const { token, logout } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('compatibility');

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API}/matches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMatches(response.data);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMatches();
  }, [token]);

  const filteredMatches = matches
    .filter(match => 
      match.user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'compatibility') {
        return b.compatibility.overall - a.compatibility.overall;
      }
      return a.user.full_name.localeCompare(b.user.full_name);
    });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-[#E8E2D2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="matches-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl text-[#2D2A26]">Intuitive Matchmaker</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link 
                to="/dashboard" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <Link 
                to="/calculator" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-calculator"
              >
                <Calculator className="w-4 h-4" />
                Calculator
              </Link>
              <Link 
                to="/settings" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-settings"
              >
                <Settings className="w-4 h-4" />
              </Link>
              <button 
                onClick={logout}
                className="text-[#8C857B] hover:text-[#D4AF37] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#8C857B] hover:text-[#D4AF37] mb-4 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
          <h1 className="font-heading text-4xl text-[#2D2A26] font-light">Your Cosmic Matches</h1>
          <p className="text-[#8C857B] mt-2">Souls whose numbers harmonize with yours</p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C857B]" />
            <Input
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11 bg-white/50 border-[#E8E2D2] focus:border-[#D4AF37] rounded-xl h-12"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={sortBy === 'compatibility' ? 'default' : 'outline'}
              onClick={() => setSortBy('compatibility')}
              className={sortBy === 'compatibility' 
                ? 'rounded-full btn-gold' 
                : 'rounded-full border-[#E8E2D2] text-[#4A453E] hover:border-[#D4AF37]'
              }
              data-testid="sort-compatibility"
            >
              <Heart className="w-4 h-4 mr-2" />
              By Compatibility
            </Button>
            <Button
              variant={sortBy === 'name' ? 'default' : 'outline'}
              onClick={() => setSortBy('name')}
              className={sortBy === 'name' 
                ? 'rounded-full btn-gold' 
                : 'rounded-full border-[#E8E2D2] text-[#4A453E] hover:border-[#D4AF37]'
              }
              data-testid="sort-name"
            >
              A-Z
            </Button>
          </div>
        </motion.div>

        {/* Matches Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="ethereal-card rounded-2xl p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-[#F3E5AB]/50" />
                  <div className="flex-1">
                    <div className="h-4 bg-[#F3E5AB]/50 rounded w-24 mb-2" />
                    <div className="h-3 bg-[#F3E5AB]/30 rounded w-32" />
                  </div>
                </div>
                <div className="compatibility-bar"><div className="compatibility-fill w-0" /></div>
              </div>
            ))}
          </div>
        ) : filteredMatches.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMatches.map((match, i) => (
              <motion.div
                key={match.user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link 
                  to={`/matches/${match.user.id}`}
                  className="block ethereal-card rounded-2xl p-6 hover:shadow-[0_8px_40px_rgba(212,175,55,0.25)] transition-all duration-300"
                  data-testid={`match-card-${match.user.id}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center text-white font-heading text-xl flex-shrink-0">
                      {match.user.full_name?.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-xl text-[#2D2A26] truncate">
                        {match.user.full_name}
                      </h3>
                      <p className="text-[#8C857B] text-sm">
                        Life Path {match.user.numerology?.life_path} • Expression {match.user.numerology?.expression}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="compatibility-bar">
                      <div 
                        className="compatibility-fill" 
                        style={{ width: `${match.compatibility.overall}%` }} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#D4AF37] font-medium text-lg">
                        {match.compatibility.overall}% Compatible
                      </span>
                      <span className="text-[#8C857B] text-sm">
                        {match.user.looking_for === 'love' ? '💕 Love' : 
                         match.user.looking_for === 'friendship' ? '🤝 Friendship' :
                         match.user.looking_for === 'guidance' ? '✨ Guidance' : '🌟 Connection'}
                      </span>
                    </div>
                  </div>

                  {match.user.bio && (
                    <p className="text-[#8C857B] text-sm mt-4 line-clamp-2 font-light">
                      "{match.user.bio}"
                    </p>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="ethereal-card rounded-2xl p-12 text-center">
            <Users className="w-12 h-12 text-[#D4AF37]/50 mx-auto mb-4" />
            <p className="text-[#8C857B]">
              {searchTerm ? 'No matches found for your search.' : 'No matches found yet.'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MatchesPage;
